/**
 * Time-zone math for interview/onboarding scheduling. Shared verbatim with Cloud Functions
 * (scripts/syncFunctionsGenerated.mjs), so the calendar the employer sees and the slot the server
 * accepts are computed by the same code.
 *
 * Every instant is stored as UTC epoch milliseconds. A recruiter's availability is written in
 * wall-clock terms in THEIR zone ("Mon 09:00–12:00, America/New_York"), and turned into UTC
 * instants here, per day, so daylight-saving changes land where they should. Display always
 * formats an instant in the viewer's zone and says which zone it is.
 *
 * Plain Intl only (no date library): the functions runtime compiles this with an ES2017 target.
 */

export const MS_PER_MINUTE = 60 * 1000;
export const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

/**
 * The ONE last-resort zone (2026-10-13 logic audit): Mountain time, where the company is based. Every
 * "which zone?" chain ends here once the stored zone, the person's state and the org's / job's state
 * are all unknown (quiet hours: resolveRecipientTimeZone; employer days: resolveOrgTimeZone; a booking
 * with no host zone; a browser that cannot say its zone). Before this, some chains ended in New York.
 */
export const DEFAULT_TIME_ZONE = 'America/Denver';

/** US zones customers are actually in, plus the ones an EMT may be in. Order = picker order. */
export const US_TIME_ZONES: Array<{ id: string; label: string }> = [
  { id: 'America/New_York', label: 'Eastern (New York, North Carolina)' },
  { id: 'America/Chicago', label: 'Central (Chicago, Texas)' },
  { id: 'America/Denver', label: 'Mountain (Denver, Utah)' },
  { id: 'America/Phoenix', label: 'Arizona (no daylight saving)' },
  { id: 'America/Los_Angeles', label: 'Pacific (California)' },
  { id: 'America/Anchorage', label: 'Alaska' },
  { id: 'Pacific/Honolulu', label: 'Hawaii' },
];

/**
 * Best-effort zone for a US state (for the candidate on an employer-booked invite, when their
 * browser has not told us). Multi-zone states use the zone most of their population is in.
 */
const STATE_ZONES: Record<string, string> = {
  AL: 'America/Chicago', AK: 'America/Anchorage', AZ: 'America/Phoenix', AR: 'America/Chicago',
  CA: 'America/Los_Angeles', CO: 'America/Denver', CT: 'America/New_York', DE: 'America/New_York',
  DC: 'America/New_York', FL: 'America/New_York', GA: 'America/New_York', HI: 'Pacific/Honolulu',
  ID: 'America/Denver', IL: 'America/Chicago', IN: 'America/New_York', IA: 'America/Chicago',
  KS: 'America/Chicago', KY: 'America/New_York', LA: 'America/Chicago', ME: 'America/New_York',
  MD: 'America/New_York', MA: 'America/New_York', MI: 'America/New_York', MN: 'America/Chicago',
  MS: 'America/Chicago', MO: 'America/Chicago', MT: 'America/Denver', NE: 'America/Chicago',
  NV: 'America/Los_Angeles', NH: 'America/New_York', NJ: 'America/New_York', NM: 'America/Denver',
  NY: 'America/New_York', NC: 'America/New_York', ND: 'America/Chicago', OH: 'America/New_York',
  OK: 'America/Chicago', OR: 'America/Los_Angeles', PA: 'America/New_York', RI: 'America/New_York',
  SC: 'America/New_York', SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago',
  UT: 'America/Denver', VT: 'America/New_York', VA: 'America/New_York', WA: 'America/Los_Angeles',
  WV: 'America/New_York', WI: 'America/Chicago', WY: 'America/Denver', PR: 'America/Puerto_Rico',
};

export function timeZoneForState(state: unknown): string | null {
  if (typeof state !== 'string') return null;
  return STATE_ZONES[state.trim().toUpperCase()] || null;
}

export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !tz || tz.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export type ZonedParts = { year: number; month: number; day: number; hour: number; minute: number; weekday: number };

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const partsFormatters: Record<string, Intl.DateTimeFormat> = {};

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsFormatters[tz];
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'short',
    } as Intl.DateTimeFormatOptions);
    partsFormatters[tz] = f;
  }
  return f;
}

/** Wall-clock fields of an instant in a zone. month is 1-12, weekday 0=Sunday. */
export function zonedParts(ms: number, tz: string): ZonedParts {
  const out: ZonedParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, weekday: 0 };
  for (const p of partsFormatter(tz).formatToParts(new Date(ms))) {
    if (p.type === 'year') out.year = Number(p.value);
    else if (p.type === 'month') out.month = Number(p.value);
    else if (p.type === 'day') out.day = Number(p.value);
    else if (p.type === 'hour') out.hour = Number(p.value) % 24;
    else if (p.type === 'minute') out.minute = Number(p.value);
    else if (p.type === 'weekday') out.weekday = WEEKDAYS[p.value] ?? 0;
  }
  return out;
}

/** Offset of the zone from UTC at that instant, in ms (e.g. -4h for EDT). */
export function zoneOffsetMs(ms: number, tz: string): number {
  const p = zonedParts(ms, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const floored = ms - (((ms % MS_PER_MINUTE) + MS_PER_MINUTE) % MS_PER_MINUTE);
  return asUtc - floored;
}

/**
 * The UTC instant at which the wall clock in `tz` reads year-month-day hour:minute. For a time
 * skipped by a spring-forward gap this returns the instant one hour later, never throws.
 */
export function zonedWallTimeToUtcMs(year: number, month: number, day: number, hour: number, minute: number, tz: string): number {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  let guess = naive - zoneOffsetMs(naive, tz);
  // A second pass settles instants near a DST transition.
  guess = naive - zoneOffsetMs(guess, tz);
  return guess;
}

/** "2026-09-21" for the calendar day an instant falls on in a zone. */
export function zonedDateKey(ms: number, tz: string): string {
  const p = zonedParts(ms, tz);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Adds whole calendar days to a Y-M-D (pure date arithmetic, zone-free). */
export function addDaysToDate(year: number, month: number, day: number, days: number): { year: number; month: number; day: number } {
  const d = new Date(Date.UTC(year, month - 1, day + days));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** Short zone name at that instant: "EDT", "PDT", "MST". */
export function zoneAbbreviation(ms: number, tz: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date(ms))
      .find((p) => p.type === 'timeZoneName');
    return part ? part.value : tz;
  } catch {
    return tz;
  }
}

/** Human zone name: "Eastern Time" style label when known, else the IANA id. */
export function zoneLongName(tz: string): string {
  const known = US_TIME_ZONES.find((z) => z.id === tz);
  if (known) return known.label.split(' (')[0] + ' Time';
  return tz.replace(/_/g, ' ');
}

export function formatDateInZone(ms: number, tz: string): string {
  return new Date(ms).toLocaleDateString('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export function formatShortDateInZone(ms: number, tz: string): string {
  return new Date(ms).toLocaleDateString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatTimeInZone(ms: number, tz: string): string {
  return new Date(ms).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
}

/** "Tuesday, September 22, 2026 · 10:00 AM – 10:30 AM EDT" */
export function formatRangeInZone(startMs: number, endMs: number, tz: string): string {
  return `${formatDateInZone(startMs, tz)} · ${formatTimeInZone(startMs, tz)} – ${formatTimeInZone(endMs, tz)} ${zoneAbbreviation(startMs, tz)}`;
}

/** "10:00 AM EDT (Eastern Time)" */
export function formatTimeWithZone(ms: number, tz: string): string {
  return `${formatTimeInZone(ms, tz)} ${zoneAbbreviation(ms, tz)} (${zoneLongName(tz)})`;
}

/** "Wed, Oct 14, 2026, 10:00 AM MDT": one instant with its date, time and zone, for lists and cards. */
export function formatDateTimeWithZone(ms: number, tz: string): string {
  const date = new Date(ms).toLocaleDateString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  return `${date}, ${formatTimeInZone(ms, tz)} ${zoneAbbreviation(ms, tz)}`;
}

/**
 * Whole calendar days from the day `fromMs` falls on to the day `toMs` falls on, both read in `tz`
 * ("posted yesterday at 11 PM" is 1 day ago at 1 AM, not 0). Negative when `toMs` is the earlier day.
 */
export function calendarDaysBetweenInZone(fromMs: number, toMs: number, tz: string): number {
  const a = zonedParts(fromMs, tz);
  const b = zonedParts(toMs, tz);
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / MS_PER_DAY);
}

/**
 * A date input ("2026-10-12") and a time input ("14:00") read as wall-clock time in `tz`, shown as
 * { date: "Monday, October 12, 2026", time: "2:00 PM EDT" }. Either part that does not parse is
 * returned as typed, so a message never loses what the employer entered.
 */
export function formatWallDateTimeInZone(date: string, time: string, tz: string): { date: string; time: string } {
  const d = date.trim();
  const t = time.trim();
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  const tm = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!dm || !tm) return { date: d, time: t };
  const ms = zonedWallTimeToUtcMs(Number(dm[1]), Number(dm[2]), Number(dm[3]), Number(tm[1]), Number(tm[2]), tz);
  if (!Number.isFinite(ms)) return { date: d, time: t };
  return { date: formatDateInZone(ms, tz), time: `${formatTimeInZone(ms, tz)} ${zoneAbbreviation(ms, tz)}` };
}

/** The browser's zone, or null when it cannot say (server side, very old browsers). */
export function detectBrowserTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(tz) ? tz : null;
  } catch {
    return null;
  }
}

/** The zone a screen shows times in: the viewer's browser zone, else DEFAULT_TIME_ZONE. */
export function viewerTimeZone(): string {
  return detectBrowserTimeZone() ?? DEFAULT_TIME_ZONE;
}
